/**
 * Tarjetas del dashboard. Cada tarjeta lleva la LISTA de registros que la componen:
 * el número mostrado es siempre `items.length` (criterio: tarjeta = tabla de detalle).
 */
import { colToIndex } from '../a1';
import { isBlank, isNumber, sheetsEquals } from '../formulas/sheets-semantics';
import type { SheetName } from '../schema';
import type { CellValue } from '../workbook';
import { inBoards, type DataRow, type RowsBySheet } from './kpis';

export type Tone = 'brand' | 'neutral' | 'ok' | 'warn' | 'bad' | 'info';

export interface Card {
  id: string;
  title: string;
  sheet: SheetName;
  items: DataRow[];
  tone: Tone;
  /** No depende del filtro por tablero (SEG-01, INS-01, INV-01). */
  global?: boolean;
  /** Texto secundario (ej. "3 vencidas"). */
  detail?: string;
}

const get = (row: DataRow, col: string): CellValue => row.v[colToIndex(col)] ?? '';
const eq = (row: DataRow, col: string, crit: string) => sheetsEquals(get(row, col), crit);
const hasCode = (row: DataRow) => !isBlank(get(row, 'A'));

const STATUS_TONE: Record<string, Tone> = {
  'Pendiente de autorización': 'info',
  Pendiente: 'neutral',
  'En proceso': 'warn',
  Detenido: 'bad',
  Terminado: 'ok',
  Entregado: 'ok',
};

export function statusCards(data: RowsBySheet, boards: Set<string> | null, statuses: string[]): Card[] {
  const ot = inBoards(data['OT-01'], 'A', boards).filter(hasCode);
  const cards: Card[] = [{ id: 'status:total', title: 'Total de tableros', sheet: 'OT-01', items: ot, tone: 'brand' }];
  // Orden de la tarjeta "Pendiente de autorización" primero, como en la referencia.
  const ordered = [...statuses].sort((a, b) => (a === 'Pendiente de autorización' ? -1 : b === 'Pendiente de autorización' ? 1 : 0));
  for (const s of ordered) cards.push({ id: `status:${s}`, title: s, sheet: 'OT-01', items: ot.filter((r) => eq(r, 'K', s)), tone: STATUS_TONE[s] ?? 'neutral' });
  const other = ot.filter((r) => !statuses.some((s) => eq(r, 'K', s)));
  if (other.length > 0) cards.push({ id: 'status:other', title: 'Sin estado / otro', sheet: 'OT-01', items: other, tone: 'neutral' });
  return cards;
}

export function stageCards(data: RowsBySheet, boards: Set<string> | null, stages: string[]): Card[] {
  const ot = inBoards(data['OT-01'], 'A', boards).filter(hasCode);
  return stages.map((s) => ({ id: `stage:${s}`, title: s, sheet: 'OT-01' as const, items: ot.filter((r) => eq(r, 'J', s)), tone: 'neutral' as const }));
}

export function alertCards(data: RowsBySheet, boards: Set<string> | null): Card[] {
  const ot = inBoards(data['OT-01'], 'A', boards).filter(hasCode);
  const ncr = inBoards(data['NCR-01'], 'C', boards).filter(hasCode);
  const ing = inBoards(data['ING-01'], 'A', boards).filter(hasCode);
  const des = inBoards(data['DES-01'], 'A', boards).filter(hasCode);
  const seg = (data['SEG-01'] ?? []).filter(hasCode);
  const ins = (data['INS-01'] ?? []).filter(hasCode);
  const inv = (data['INV-01'] ?? []).filter(hasCode);

  const late = ot.filter((r) => {
    const p = get(r, 'P');
    return isNumber(p) && p > 0 && isBlank(get(r, 'I')) && !eq(r, 'K', 'Entregado');
  });
  const unsafe = seg.filter((r) => eq(r, 'C', 'Condición insegura') && eq(r, 'N', 'Abierta'));
  const unsafeOverdue = unsafe.filter((r) => eq(r, 'O', 'Vencida')).length;
  const unfit = ins.filter((r) => ['Vencido', 'No usar', 'Sin calibración verificable'].some((s) => eq(r, 'M', s)));
  const dueSoon = ins.filter((r) => eq(r, 'M', 'Por vencer'));
  const stock = inv.filter((r) => eq(r, 'M', 'Faltante') || eq(r, 'M', 'Reponer'));
  const shortage = stock.filter((r) => eq(r, 'M', 'Faltante')).length;

  return [
    { id: 'alert:late', title: 'Atrasados', sheet: 'OT-01', items: late, tone: 'bad' },
    { id: 'alert:ncr', title: 'NCR abiertas o en corrección', sheet: 'NCR-01', items: ncr.filter((r) => eq(r, 'N', 'Abierta') || eq(r, 'N', 'En corrección')), tone: 'warn' },
    { id: 'alert:unsafe', title: 'Condiciones inseguras abiertas', sheet: 'SEG-01', items: unsafe, tone: 'bad', global: true, detail: `${unsafeOverdue} vencida(s)` },
    { id: 'alert:instruments', title: 'Instrumentos no aptos', sheet: 'INS-01', items: unfit, tone: 'bad', global: true },
    { id: 'alert:instrumentsSoon', title: 'Instrumentos por vencer', sheet: 'INS-01', items: dueSoon, tone: 'warn', global: true },
    { id: 'alert:stock', title: 'Materiales en Faltante o Reponer', sheet: 'INV-01', items: stock, tone: 'warn', global: true, detail: `${shortage} en faltante` },
    { id: 'alert:drawings', title: 'Planos con alerta', sheet: 'ING-01', items: ing.filter((r) => !eq(r, 'K', 'OK')), tone: 'warn' },
    { id: 'alert:released', title: 'Tableros LIBERADOS', sheet: 'DES-01', items: des.filter((r) => eq(r, 'L', 'LIBERADO')), tone: 'ok' },
    { id: 'alert:notReleased', title: 'Tableros NO LIBERAR', sheet: 'DES-01', items: des.filter((r) => eq(r, 'L', 'NO LIBERAR')), tone: 'bad' },
  ];
}
